import { useEffect, useState } from "react";
import { Power, Wrench, Eye, ShieldAlert, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { FullSpinner } from "@/components/States";
import { toast } from "sonner";

const MODES = [
  { key: "operational", name: "Operational", icon: CheckCircle2, desc: "Everything running normally for all members.", cls: "border-emerald-600/50 text-emerald-300", active: "bg-emerald-900/40" },
  { key: "maintenance", name: "Maintenance", icon: Wrench, desc: "Members see a maintenance notice; the member app is paused. Admins keep full access.", cls: "border-amber-600/50 text-amber-300", active: "bg-amber-900/40" },
  { key: "read_only", name: "Read-Only", icon: Eye, desc: "Members can browse but cannot make changes (no sends, edits or payments).", cls: "border-sky-600/50 text-sky-300", active: "bg-sky-900/40" },
  { key: "lockdown", name: "Emergency Lockdown", icon: ShieldAlert, desc: "The entire member app is blocked. Use only for security incidents.", cls: "border-rose-600/50 text-rose-300", active: "bg-rose-900/40" },
];

export default function AdminPlatformControl() {
  const [state, setState] = useState(null);
  const [meta, setMeta] = useState({});
  const [sel, setSel] = useState("operational");
  const [msg, setMsg] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/platform-state").then(({ data }) => { setState(data.state); setMeta(data.meta || {}); setSel(data.state.mode); setMsg(data.state.message || ""); });
  useEffect(() => { load(); }, []);

  const apply = async () => {
    if (sel !== "operational" && !reason.trim()) return toast.error("A reason is required for non-operational states.");
    setSaving(true);
    try {
      await api.put("/admin/platform-state", { mode: sel, message: msg, reason });
      toast.success(`Platform set to ${sel.replace("_", " ")}`);
      setReason("");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!state) return <FullSpinner label="Loading platform control…" />;
  const current = state.mode;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-platform-control">
      <div>
        <h1 className="font-serif text-3xl font-bold flex items-center gap-2"><Power className="w-6 h-6 text-[#C59B27]" /> Platform Control</h1>
        <p className="text-sm text-[#D8B4B9] mt-1">Global operating state for the entire member application. Admins always retain access. Every change is audited.</p>
      </div>

      <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4 text-sm">
        Current state: <span className="font-semibold capitalize text-[#E2C382]">{current.replace("_", " ")}</span>
        {meta.updated_at && <span className="text-[#8A6D71]"> · updated {new Date(meta.updated_at).toLocaleString()}</span>}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {MODES.map((m) => {
          const on = sel === m.key;
          return (
            <button key={m.key} onClick={() => setSel(m.key)} data-testid={`mode-${m.key}`}
              className={`text-left p-4 rounded-2xl border transition-all ${on ? `${m.cls} ${m.active}` : "border-[#781826]/40 text-[#D8B4B9] hover:border-[#781826]"}`}>
              <m.icon className="w-5 h-5 mb-2" />
              <div className="font-semibold">{m.name}{current === m.key && <span className="ml-2 text-[10px] uppercase tracking-wide text-[#C59B27]">live</span>}</div>
              <p className="text-xs mt-1 opacity-80">{m.desc}</p>
            </button>
          );
        })}
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-xs text-[#C59B27] uppercase tracking-wide">Member-facing message (optional)</label>
          <input value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="platform-message"
            placeholder="e.g. We'll be back by 6 PM IST. Thank you for your patience."
            className="w-full mt-1 bg-[#1A090D] border border-[#781826]/40 rounded-lg px-3 py-2.5 text-sm text-white" />
        </div>
        {sel !== "operational" && (
          <div>
            <label className="text-xs text-[#C59B27] uppercase tracking-wide">Reason (required, audited)</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="platform-reason"
              placeholder="Why are you changing the platform state?"
              className="w-full mt-1 bg-[#1A090D] border border-[#781826]/40 rounded-lg px-3 py-2.5 text-sm text-white" />
          </div>
        )}
        <button onClick={apply} disabled={saving || sel === current} data-testid="apply-platform-state"
          className="btn-gold rounded-xl px-6 py-2.5 disabled:opacity-50">
          {saving ? "Applying…" : sel === current ? "No change" : `Apply “${MODES.find((m) => m.key === sel)?.name}”`}
        </button>
      </div>
    </div>
  );
}
